/**
 * The location page's trade-context strip.
 *
 * Lifted verbatim out of `sites/trade-panel.tsx` (#1011) as part of the
 * pure-move commit: no behaviour changed here, so the diff is a rename
 * and the only question a reviewer has to answer is "did anything
 * change?". The IA flip that consumes it is the next commit.
 */
import { formatDate } from "@/lib/utils";
import { StatTile, type StatTileTone } from "@/components/ui/stat-tile";
import { Droplets, Building2 } from "lucide-react";
import type { SiteContext, TradeSummary } from "./types";

export function TradeContextStrip({
    site,
    tradeCode,
    summary,
}: {
    site: SiteContext;
    tradeCode: string;
    summary: TradeSummary;
}) {
    // Compact KPI tiles. Numbers come from /summary so the strip
    // matches what the Hub header already shows.
    const tiles: Array<{ label: string; value: string; tone?: StatTileTone }> = [
        { label: "Devices", value: String(summary.device_count) },
    ];
    if (summary.overdue_count > 0) {
        tiles.push({
            label: "Overdue",
            value: String(summary.overdue_count),
            tone: "danger",
        });
    } else if (summary.due_soon_count > 0) {
        tiles.push({
            label: "Due 90d",
            value: String(summary.due_soon_count),
            tone: "warn",
        });
    }
    tiles.push({ label: "Open jobs", value: String(summary.open_job_count) });
    if (summary.last_inspection_at) {
        tiles.push({
            label: "Last test",
            value: formatDate(summary.last_inspection_at),
        });
    }

    const isBackflow = tradeCode === "backflow";

    return (
        <div className="flex flex-wrap items-stretch gap-3">
            {/* These tiles render OUTSIDE any <Card>, straight onto the shell
                (`--color-shell` is #44403c — dark warm grey — in LIGHT mode).
                They used to append `bg-destructive/5` to a base containing
                `bg-card`; twMerge drops the earlier background, so the OVERDUE
                tile was near-black text on dark grey in the default theme.
                StatTile makes that shape impossible. */}
            {tiles.map((t) => (
                <StatTile key={t.label} label={t.label} value={t.value} tone={t.tone ?? "neutral"} />
            ))}
            {isBackflow && (site.waterProviderName || site.accountNumber) && (
                <div className="rounded-lg border bg-card px-4 py-2 flex-1 min-w-[240px]">
                    <div className="text-[10px] uppercase tracking-wide text-foreground/80">Jurisdiction</div>
                    <div className="flex items-center gap-2 flex-wrap mt-0.5">
                        {site.waterProviderName && (
                            <span className="text-sm font-medium inline-flex items-center gap-1">
                                <Droplets className="h-3.5 w-3.5 text-foreground/80" />
                                {site.waterProviderName}
                                {site.waterProviderSubmissionPlatform && (
                                    <span className="text-xs text-foreground/80">
                                        via {site.waterProviderSubmissionPlatform}
                                    </span>
                                )}
                            </span>
                        )}
                        {site.accountNumber && (
                            <span className="text-sm font-mono inline-flex items-center gap-1 text-foreground/80">
                                <Building2 className="h-3.5 w-3.5" /> {site.accountNumber}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Devices ──────────────────────────────────────────────────────────
